import {
  DocumentToStoreState,
  DocumentToStoreMutationTypes,
  LockModel,
  ErrorModel,
  DocumentToStoreModel,
  DocumentToStoreViewModel,
  SchemaModel,
  KeyValuePair,
  Cabinet,
  ExistingDocumentOption
} from '@/types';
import { MutationTree } from 'vuex';

export const mutations: MutationTree<DocumentToStoreState> = {
  [DocumentToStoreMutationTypes.SetIsLoading](state, isLoading: boolean) {
    state.isLoading = isLoading;
  },
  [DocumentToStoreMutationTypes.SetIsDirty](state, isDirty: boolean) {
    state.isDirty = isDirty;
  },
  [DocumentToStoreMutationTypes.SetIsReadonly](state, isReadonly: boolean) {
    state.isReadonly = isReadonly;
  },
  [DocumentToStoreMutationTypes.SetExtendingDocumentLock](state, payload: boolean) {
    state.extendingDocumentToStoreLock = payload;
  },
  [DocumentToStoreMutationTypes.ExtendDocumentLock](state, payload: LockModel) {
    state.documentToStore.lock = payload;
  },
  [DocumentToStoreMutationTypes.ExpireDocumentLock](state, payload: any) {
    if (payload) {
      state.errorMessage = payload;
    }
    state.documentToStore.lock = {} as LockModel;
  },
  [DocumentToStoreMutationTypes.DocumentToStoreLoaded](state, payload: DocumentToStoreModel) {
    state.documentToStore = payload;
    state.isLoading = false;
    state.error = false;
  },
  [DocumentToStoreMutationTypes.DocumentToStoreError](state, payload: any) {
    state.errorMessage = JSON.stringify(payload);
    state.documentToStore = {} as DocumentToStoreModel;
    state.isLoading = false;
    state.error = true;
  },
  [DocumentToStoreMutationTypes.SetComponentName](state, componentName: string) {
    state.documentToStore.componentName = componentName;
  },
  [DocumentToStoreMutationTypes.SetErrorModel](state, errorModel: ErrorModel) {
    state.documentToStore.errorModel = errorModel;
  },
  [DocumentToStoreMutationTypes.SetDocumentToStoreView](state, payload: DocumentToStoreViewModel) {
    state.documentToStoreView = payload;
    state.initialSchema = payload.dynamicFormSchema.map((model) => {
      return { ...model };
    });
  },
  [DocumentToStoreMutationTypes.SetDynamicFormSchema](state, payload: SchemaModel[]) {
    state.documentToStoreView.dynamicFormSchema = payload;
  },
  [DocumentToStoreMutationTypes.SetMultiValueReferenceValues](state, payload: any[]) {
    state.documentToStoreView.multiValueReferences = payload;
  },
  [DocumentToStoreMutationTypes.SetSchemaValueFromReference](state, reference: KeyValuePair<string>) {
    const sourceCabinet = state.documentToStoreView.cabinets?.find((cabinet) => cabinet.documentTypes.some((type) => type.id.toString() === state.selectedSourceDocumentTypeId.toString()));
    const sourceDocumentTypeDescription = sourceCabinet?.documentTypes?.find((type) => type.id.toString() === state.selectedSourceDocumentTypeId.toString())?.description;
    const targetCabinet = state.documentToStoreView.cabinets?.find((cabinet) => cabinet.documentTypes.some((type) => type.id.toString() === state.documentToStoreView.targetDocumentTypeId.toString()));
    if (
      sourceCabinet?.id === Cabinet.IncomingDocuments &&
      sourceDocumentTypeDescription?.toLowerCase() === Cabinet.DocumentToStoreDocumentTypeDescription.toLowerCase() &&
      targetCabinet?.id === Cabinet.IncomingDocuments &&
      reference.key.toLowerCase() === Cabinet.MyIncomingDocumentsReferenceKey.toLowerCase()
    ) {
      return;
    }
    const stateSchema = state.documentToStoreView.dynamicFormSchema.find((schema) => schema.id.toLowerCase() === reference.key.toLowerCase());

    if (stateSchema) {
      stateSchema.value = reference.value;
    }
  },
  [DocumentToStoreMutationTypes.SetDocumentDateValue](state, documentDate: Date | null) {
    const documentDateSchema = state.documentToStoreView.dynamicFormSchema.find((schema) => schema.id.toLowerCase() === Cabinet.DocumentDateReference.FieldKey.toLowerCase());
    if (documentDateSchema) {
      documentDateSchema.value = documentDate;
    }
  },
  [DocumentToStoreMutationTypes.SetSourceDocumentTypeId](state, documentTypeId: number) {
    state.selectedSourceDocumentTypeId = documentTypeId;
  },
  [DocumentToStoreMutationTypes.SetExistingDocumentId](state, documentId: number) {
    state.existingDocumentId = documentId;
  },
  [DocumentToStoreMutationTypes.SetExistingDocumentOption](state, option: ExistingDocumentOption) {
    state.existingDocumentOption = option;
  },
  [DocumentToStoreMutationTypes.SetInitialSchemaValues](state, { initialSchema, initialDocumentDate }) {
    state.initialSchema = initialSchema.map((model: SchemaModel) => {
      return { ...model };
    });
    state.initialDocumentDate = initialDocumentDate;
  },
  [DocumentToStoreMutationTypes.SetInitialMultiValueReferences](state, initialMultiValues: any[]) {
    state.initialMultiValueReferences = initialMultiValues;
  },
  [DocumentToStoreMutationTypes.SetKeepPagesAfterStoring](state, value: boolean) {
    state.keepPagesAfterStoring = value;
  },
  [DocumentToStoreMutationTypes.SetSelectedPages](state, value: string) {
    state.selectedPages = value;
  },
  [DocumentToStoreMutationTypes.SetDocumentStatus](state, value: string) {
    state.documentToStore.status = value;
  },
  [DocumentToStoreMutationTypes.SetPageViewedBeforeAddToExisting](state, page: number) {
    state.pageViewedBeforeAddToExisting = page;
  },
  [DocumentToStoreMutationTypes.SetAllPagesSelectedWithManualPageSelection](state, allPagesSelectedWithManualPageSelection: boolean) {
    state.allPagesSelectedWithManualPageSelection = allPagesSelectedWithManualPageSelection;
  },
  [DocumentToStoreMutationTypes.SetAllPagesSelectedWithBatchSplitting](state, allPagesSelectedWithBatchSplitting: boolean) {
    state.allPagesSelectedWithBatchSplitting = allPagesSelectedWithBatchSplitting;
  },
  [DocumentToStoreMutationTypes.SetReferencesForNextDocument](state, references: SchemaModel[]) {
    state.referencesForNextDocument = references;
  },
  [DocumentToStoreMutationTypes.SetMultiValueReferencesForNextDocument](state, references: any[]) {
    state.multiValueReferencesForNextDocument = references;
  },
  [DocumentToStoreMutationTypes.ApplyReferencesToNextDocumentEnabled](state, value: boolean) {
    state.applyReferencesToNextDocumentEnabled = value;
  },
  [DocumentToStoreMutationTypes.ClearReferencesForNextDocument](state) {
    state.referencesForNextDocument = [];
    state.multiValueReferencesForNextDocument = [];
  }
};
